<template>
    <div class="p-attachment">
        <div
            class="attachment-upload"
            :class="{ 'attachment-drag-hover': isDragActive, 'attachment-started': files.length > 0 }"
            @dragover.prevent="onDragOver"
            @dragleave="onDragLeave"
            @drop.prevent="onDrop"
            @click="openFilePicker">
            <input
                ref="fileInput"
                type="file"
                style="display: none;"
                :accept="acceptAttribute"
                :multiple="maxFiles !== 1"
                @change="onFileInputChange"
            />
            <div class="attachment-message">
                <em class="fa fa-upload fa-2x text-muted d-block mb-2"></em>
                <div class="font-weight-medium">Drop file to upload</div>
                <div class="text-muted" style="font-size: 0.85rem;">or click to browse</div>
            </div>
            <div v-for="(file, index) in files" :key="file.id" class="attachment-preview">
                <div class="attachment-file-row d-flex align-items-center py-1 px-2 mb-1 border rounded bg-light">
                    <em class="fa fa-file-o me-2 text-secondary flex-shrink-0"></em>
                    <span class="attachment-filename flex-grow-1 text-truncate">{{ file.name }}</span>
                    <span class="text-muted ms-3 flex-shrink-0 small">{{ formatSize(file.size) }}</span>
                    <span
                        class="ms-3 flex-shrink-0 text-danger"
                        style="cursor: pointer;"
                        title="Remove"
                        @click.stop="removeAt(index)">
                        <em class="fa fa-times"></em>
                    </span>
                </div>
                <div v-if="file.error" class="text-danger small px-2">
                    {{ file.error }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
let nextFileId = 0;

export default {
    name: 'attachment',
    props: {
        accept: {
            type: String,
            default: null
        },
        maxFiles: {
            type: Number,
            default: null
        },
        maxFileSizeMb: {
            type: Number,
            default: 10
        },
        acceptedFileTypes: {
            type: Array,
            default: () => ['.doc', '.docx', '.xls', '.xlsx', '.tif', '.tiff', '.pdf', '.jpg', '.jpeg', '.png', '.gif', '.svg', '.webp', '.txt']
        }
    },
    emits: ['file-added', 'file-removed', 'file-error'],
    data() {
        return {
            files: [],
            isDragActive: false
        };
    },
    computed: {
        acceptAttribute() {
            return this.accept || (this.acceptedFileTypes ? this.acceptedFileTypes.join(',') : undefined);
        }
    },
    methods: {
        openFilePicker() {
            this.$refs.fileInput.click();
        },
        onDragOver() {
            this.isDragActive = true;
        },
        onDragLeave() {
            this.isDragActive = false;
        },
        onDrop(event) {
            this.isDragActive = false;
            this.processFiles(Array.from(event.dataTransfer.files));
        },
        onFileInputChange(event) {
            this.processFiles(Array.from(event.target.files));
            event.target.value = '';
        },
        processFiles(newFiles) {
            for (const file of newFiles) {
                if (this.maxFiles != null && this.files.length >= this.maxFiles) {
                    this.$emit('file-error', { file, message: `Cannot add more than ${this.maxFiles} files.` });
                    continue;
                }
                const nameError = this.validateFileName(file.name);
                if (nameError) {
                    this.files.push({ id: nextFileId++, name: file.name, size: file.size, file, error: nameError });
                    this.$emit('file-error', { file, message: nameError });
                    continue;
                }
                if (!this.isFileTypeAccepted(file)) {
                    const message = `File type not allowed. Accepted types: ${this.acceptedFileTypes.join(', ')}`;
                    this.files.push({ id: nextFileId++, name: file.name, size: file.size, file, error: message });
                    this.$emit('file-error', { file, message });
                    continue;
                }
                if (file.size > this.maxFileSizeMb * 1024 * 1024) {
                    const message = `File is too large. Maximum size is ${this.maxFileSizeMb} MB.`;
                    this.files.push({ id: nextFileId++, name: file.name, size: file.size, file, error: message });
                    this.$emit('file-error', { file, message });
                    continue;
                }
                this.files.push({ id: nextFileId++, name: file.name, size: file.size, file });
                this.$emit('file-added', file);
            }
        },
        validateFileName(name) {
            if (name.length > 260) {
                return `File name exceeds the 260 character limit.`;
            }
            // Windows reserved characters
            if (/[\\/:*?"<>|]/.test(name)) {
                return `File name contains invalid characters. The following are not allowed: \\ / : * ? " < > |`;
            }

            return null;
        },
        isFileTypeAccepted(file) {
            if (!this.acceptedFileTypes || this.acceptedFileTypes.length === 0) return true;
            const ext = '.' + file.name.split('.').pop().toLowerCase();
            return this.acceptedFileTypes.map(e => e.toLowerCase()).includes(ext);
        },
        formatSize(bytes) {
            if (bytes == null) return '';
            const kb = bytes / 1024;
            if (kb < 1024) return `${kb.toFixed(1)} KB`;
            return `${(kb / 1024).toFixed(2)} MB`;
        },
        removeAt(index) {
            const [removed] = this.files.splice(index, 1);
            if (removed) this.$emit('file-removed', removed.file);
        },
        clearFiles() {
            const removed = this.files.splice(0);
            removed.forEach(r => this.$emit('file-removed', r.file));
        },
        removeFile(file) {
            const index = this.files.findIndex(f => f.file === file);
            if (index !== -1) this.removeAt(index);
        },
        getFiles() {
            return this.files.filter(f => !f.error).map(f => f.file);
        }
    }
};
</script>

<style scoped lang="scss">
@import '@/styles/app/common/variables.scss';

.attachment-upload {
    border: 2px dashed $input-default-border-color !important;
    border-radius: 4px;
    padding: 1rem;
    width: 100%;
    background: #fafafa;
    cursor: pointer;
    transition: border-color 0.15s ease, background-color 0.15s ease;

    &:hover,
    &.attachment-drag-hover {
        border-color: #007bff !important;
        background: #f0f7ff;
    }

    .attachment-message {
        margin: 0;
        padding: 0.75rem 0;
        text-align: center;
        cursor: pointer;
    }

    // Keep the upload prompt visible after files are added
    &.attachment-started .attachment-message {
        display: block;
        border-bottom: 1px solid #e9ecef;
        padding-bottom: 0.75rem;
        margin-bottom: 0.5rem;
    }

    .attachment-preview {
        margin: 0;
        min-height: unset;
    }

    .attachment-file-row {
        font-size: 0.875rem;

        .attachment-filename {
            max-width: 300px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }
}
</style>
